import { ThemeProvider } from "@mui/material/styles";
import React, {
  PropsWithChildren,
  createContext,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import { createAppTheme } from "../../components/theme/appTheme";

type ColorMode = "light" | "dark";

type ColorModeContextType = {
  mode: ColorMode;
  setMode: () => void;
};

export const ColorModeContext = createContext<ColorModeContextType>(
  {} as ColorModeContextType
);

// localStorage / 시스템 설정에서 초기 모드를 안전하게 결정한다.
const getInitialMode = (): ColorMode => {
  const stored = window.localStorage.getItem("colorMode");
  if (stored === "light" || stored === "dark") {
    return stored;
  }
  const prefersDark = window?.matchMedia?.(
    "(prefers-color-scheme: dark)"
  ).matches;
  return prefersDark ? "dark" : "light";
};

// 첫 페인트 플래시를 줄이기 위해 모듈 로드 시점에 <html data-theme>를 미리 세팅
if (typeof document !== "undefined") {
  document.documentElement.dataset.theme = getInitialMode();
}

export const ColorModeContextProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [mode, setMode] = useState<ColorMode>(getInitialMode);

  // .module.css의 다크 토큰(:root[data-theme="dark"])을 활성화하는 속성 — 페인트 전에 반영
  useLayoutEffect(() => {
    document.documentElement.dataset.theme = mode;
  }, [mode]);

  useEffect(() => {
    window.localStorage.setItem("colorMode", mode);
  }, [mode]);

  const setColorMode = () => {
    setMode((prev) => (prev === "light" ? "dark" : "light"));
  };

  return (
    <ColorModeContext.Provider
      value={{
        setMode: setColorMode,
        mode,
      }}
    >
      <ThemeProvider theme={createAppTheme(mode)}>{children}</ThemeProvider>
    </ColorModeContext.Provider>
  );
};
